import type { OverrideInvoiceConflict } from './customers';
import { apiClient } from './client';

/**
 * A figure the API could compute only partially, or a caveat the reader needs
 * to judge the number by. `message` is a complete sentence and is rendered
 * as-is — the point is that a partial total never reaches the screen looking
 * whole.
 */
export interface FinanceDataIssue {
  code: string;
  severity: 'error' | 'warning';
  message: string;
}

// ─── Invoices & cash ───

export interface OverdueInvoice {
  invoiceNumber: string;
  companyName: string;
  total: number;
  currency: string;
  dueDate: string;
  /** ≤ 0 means not yet due (days until due, negated). */
  daysOverdue: number;
}

/** Booked in Odoo but dated after today: not owed yet, so in none of the totals. */
export interface ScheduledInvoice {
  invoiceNumber: string;
  companyName: string;
  total: number;
  currency: string;
  issueDate: string;
  dueDate: string;
}

export interface AgingBucket {
  label: string;
  amount: number;
  count: number;
  invoices: OverdueInvoice[];
}

export type InvoiceRange = 'all' | 'ytd' | '12m' | 'mtd';

export interface CreditNoteRow {
  invoiceNumber: string;
  companyName: string;
  total: number;
  residual: number;
  issueDate: string;
  /** The reversed invoice of the period it cleared, when one matches. */
  clears: string | null;
  /** refund = paid back by bank; invoice = set against `against`. Optional: older API builds lack it. */
  settledBy?: 'refund' | 'invoice' | 'netted' | null;
  against?: string | null;
  settledOn?: string | null;
}

export interface InvoicesSummary {
  currency: string;
  range: InvoiceRange;
  invoicedTotal: number;
  collectedTotal: number;
  /** Credit notes issued in the range (negative), disclosed rather than netted. */
  creditNotesTotal: number;
  /** Invoices killed by a credit note rather than paid. */
  reversedTotal: number;
  /** Positive invoices issued in the period = collected + reversed + openOnPeriod (+ unsynced). */
  grossInvoicedTotal: number;
  /** Open balance today on the positive invoices issued in the period. */
  openOnPeriodTotal: number;
  /**
   * creditNotesTotal = −reversedTotal + creditUnappliedTotal + creditOtherTotal.
   * Unapplied: credit still open (already netted in outstanding). Other:
   * settled another way, a refund or an invoice from before the period.
   */
  creditUnappliedTotal: number;
  creditOtherTotal: number;
  creditNotes: CreditNoteRow[];
  outstandingTotal: number;
  overdueTotal: number;
  aging: AgingBucket[];
  overdueInvoices: OverdueInvoice[];
  /** Documents whose open balance has not synced; excluded from the figures above. */
  unknownResidualCount: number;
  unknownResidualInvoiced: number;
  /** Invoices dated after today, left out of every figure above. */
  scheduledTotal: number;
  scheduledInvoices: ScheduledInvoice[];
  /** First day of the period (YYYY-MM-DD); null for all time. Optional: older API builds lack it. */
  periodStart?: string | null;
  /** Day after the period (YYYY-MM-DD, exclusive); null when it runs to today. */
  periodEnd?: string | null;
  /** Customer payments dated in the period: on the period's invoices, and on older ones. */
  paidInPeriod?: { onPeriodInvoices: number; onEarlierInvoices: number } | null;
  issues: FinanceDataIssue[];
}

export async function getInvoicesSummary(
  range: InvoiceRange = 'all',
  months?: number,
  fullMonths = false,
): Promise<{ summary: InvoicesSummary }> {
  const params = months ? { range, months, ...(fullMonths ? { fullMonths: 'true' } : {}) } : { range };
  return apiClient.get('/finance/invoices-summary', { params });
}

// ─── Subscriptions & renewals ───

export interface PlanCompany {
  /** The Attio name where the Odoo partner links to a customer, else the Odoo name. */
  name: string;
  /** The Odoo legal name when it differs from `name`. */
  odooName: string | null;
  mrr: number;
  /** Set when reported under a different term than Odoo's, e.g. "billed quarterly, contract yearly". */
  termNote?: string;
}

export interface BreakdownItem {
  label: string;
  count: number;
  mrr: number;
  companies: PlanCompany[];
}

export interface ContractRow {
  id: string;
  /** The Attio name where the Odoo partner links to a customer, else the Odoo name. */
  companyName: string;
  /** The Odoo legal name when it differs from `companyName`. */
  odooName: string | null;
  planName: string;
  status: string;
  /** EUR. NULL = Odoo had no rate to convert the order's currency. */
  mrrEur: number | null;
  /** The contract's own figure and currency. */
  sourceMrr: number;
  sourceCurrency: string;
  billingCycle: string | null;
  endDate: string | null;
  renewalDate: string | null;
  /** Effective expiry; null = ongoing/auto-renew. */
  expiryDate: string | null;
  /** Days until expiry (negative = lapsed); null = ongoing/auto-renew. */
  daysLeft: number | null;
  /** Earlier orders a renewal chain folded into this row; `expiryDate` is the chain's end. */
  renewedOrders: number;
}

/** One customer whose MRR is a reporting override, not its Odoo contract amount. */
export interface MrrOverrideLine {
  customer: string;
  note: string;
  contractMrr: number;
  reportedMrr: number;
  delta: number;
}

/** total = byTermTotal + overridesTotal + unexplained (0 unless something drifted). */
export interface MrrBridge {
  month: string;
  total: number;
  byTermTotal: number;
  /** `name` is the Customers tab's (Attio) name; `customer` the Odoo partner. */
  overrides: (MrrOverrideLine & {
    name: string;
    /** The counted contract in its own currency. Optional: older API builds lack it. */
    contractSource?: { mrr: number; currency: string } | null;
    /** Override and invoice disagree for the term. Optional. */
    invoiceConflict?: OverrideInvoiceConflict | null;
  })[];
  overridesTotal: number;
  unexplained: number;
}

/**
 * What an override did to a movement while Odoo's contracts stood still:
 * ran out with MRR back up to Odoo (`restored`), ran out with MRR down to Odoo
 * (`ended`), began (`started`), or replaced another override (`changed`).
 */
export type OverrideMovementKind = 'restored' | 'ended' | 'started' | 'changed';

/** A movement that exists because a reporting override starts, ends or changes. */
export interface OverrideMovementNote {
  month: string;
  customer: string;
  movementType: 'new' | 'expansion' | 'contraction' | 'churn';
  from: number;
  to: number;
  odooFrom: number;
  odooTo: number;
  /** Raw, as the sheet typed it. Display `fromNote` / `toNote` instead. */
  reason: string;
  /** null when Odoo's contracts moved too: then it is a real movement. */
  kind: OverrideMovementKind | null;
  fromNote: string | null;
  toNote: string | null;
  /** `kind === 'restored'`. */
  restoresOdooRate: boolean;
}

/** One month's movements with every override-only movement taken out. */
export interface OverrideSplit {
  newMrr: number;
  expansionMrr: number;
  contractionMrr: number;
  churnedMrr: number;
  /** Overrides running out, MRR back up to Odoo. Never negative. */
  restoredMrr: number;
  /** Overrides starting, changing or ending downwards, signed. */
  overrideChangedMrr: number;
  /** new + expansion − contraction − churn without any override movement. */
  newBusinessMrr: number;
  /** The engine's net: newBusinessMrr + restoredMrr + overrideChangedMrr. */
  netMrr: number;
}

export interface CustomerNames {
  /** The Attio name when the Odoo partner links to a customer, else the Odoo name. */
  name: string;
  odooName: string | null;
}

export interface SubscriptionsSummary {
  currency: string;
  /** Contracts counted in this month's MRR. */
  activeSubscriptions: number;
  /** Customers billing something this month. */
  payingCustomers: number;
  currentMrr: number;
  arpa: number;
  byPlan: BreakdownItem[];
  /** Σ byPlan = bridge.byTermTotal. */
  byPlanMrr: number;
  bridge: MrrBridge;
  overrideMovements: OverrideMovementNote[];
  /** Per `YYYY-MM`: the movement split with override movements out. */
  overrideSplits: Record<string, OverrideSplit>;
  /** MRR engine customer (Odoo partner) → the name the Customers tab shows. */
  customerNames: Record<string, CustomerNames>;
  upcomingRenewals: { next30: number; next60: number; next90: number };
  contracts: ContractRow[];
  issues: FinanceDataIssue[];
}

export async function getSubscriptionsSummary(): Promise<{ summary: SubscriptionsSummary }> {
  return apiClient.get('/finance/subscriptions-summary');
}

// ─── Income vs expenses ───

export interface IncomeExpensePoint {
  month: string;
  income: number;
  expenses: number;
  net: number;
}

export interface IncomeVsExpenses {
  currency: string;
  /** What the series measures, for the card to state rather than imply. */
  basis: string;
  series: IncomeExpensePoint[];
  issues: FinanceDataIssue[];
}

export async function getIncomeVsExpenses(months?: number): Promise<IncomeVsExpenses> {
  return apiClient.get('/finance/income-expenses', {
    params: months ? { months } : undefined,
  });
}

export interface MonthDetailItem {
  invoiceNumber: string;
  companyName: string;
  total: number;
  status: string;
  issueDate: string;
  /** The month this falls in is the month of the chart bar. */
  accountingDate: string;
  accountCode?: string | null;
  accountName?: string | null;
  /** Journal items rolled up into this row. */
  lineCount?: number;
}

export interface IncomeExpensesMonthDetail {
  month: string;
  currency: string;
  basis: string;
  incomeTotal: number;
  expensesTotal: number;
  net: number;
  incomeItems: MonthDetailItem[];
  expenseItems: MonthDetailItem[];
  issues: FinanceDataIssue[];
}

export async function getIncomeExpensesMonthDetail(month: string): Promise<IncomeExpensesMonthDetail> {
  return apiClient.get('/finance/income-expenses/month-detail', { params: { month } });
}

// ─── Cash movement (bank accounts) ───

export interface CashMovementPoint {
  month: string;
  moneyIn: number;
  moneyOut: number;
  net: number;
}

export interface TransferPair {
  outLineId: string;
  inLineId: string;
  outDate: string;
  inDate: string;
  fromJournal: string;
  toJournal: string;
  amount: number;
  currency: string;
  amountIn: number;
  currencyIn: string;
  daysApart: number;
  amountGap: number;
  evidence: string[];
  /** Why a possible pair was not matched outright; null for a match. */
  doubt: string | null;
}

export interface CashMovement {
  currency: string;
  series: CashMovementPoint[];
  /** What left our accounts in transfers to our own other accounts (each transfer once). */
  excludedTransfersAmount: number;
  /** What arrived from them; absent on older APIs. */
  excludedTransfersInAmount?: number;
  excludedTransfersCount: number;
  /** Unreconciled transfers paired automatically; excluded from the series. */
  autoMatchedTransfers: { count: number; amount: number; pairs: TransferPair[] };
  /** Near-misses; still counted in the series. */
  possibleTransfers: TransferPair[];
  /** Last statement line per bank journal: how far each account is imported. Absent on older APIs. */
  statementCoverage?: Array<{ journal: string; lastLineDate: string; longestGapDays: number | null }>;
  /** A journal whose statements stopped arriving (error), or what a €0 money-in month means (warning). */
  issues?: FinanceDataIssue[];
  /** Money in that did not land on a customer receivable, biggest first (capped), and per month. Optional. */
  notCustomerIn?: {
    lines: Array<{
      date: string;
      amount: number;
      journal: string | null;
      partnerName: string | null;
      paymentRef: string | null;
      counterpartAccountCode: string | null;
    }>;
    byMonth: Record<string, number>;
  };
  /** Returned payments netted against the outflow they reversed. Optional: older builds lack it. */
  returnedPayments?: {
    count: number;
    amount: number;
    lines: Array<{
      returnLineId: string;
      date: string;
      amount: number;
      journalName: string | null;
      paymentRef: string | null;
      originalLineId: string | null;
      originalDate: string | null;
    }>;
  };
}

export async function getCashMovement(months?: number): Promise<CashMovement> {
  return apiClient.get('/finance/cash-movement', {
    params: months ? { months } : undefined,
  });
}

// ─── MRR vs P&L expenses ───

export interface MrrExpensePoint {
  month: string;
  mrr: number;
  expenses: number;
  net: number;
}

export interface MrrVsExpenses {
  currency: string;
  /** What the series measures — the two P&L cards do not measure the same thing. */
  basis: string;
  series: MrrExpensePoint[];
  issues: FinanceDataIssue[];
}

export async function getMrrVsExpenses(months?: number): Promise<MrrVsExpenses> {
  return apiClient.get('/finance/mrr-vs-expenses', {
    params: months ? { months } : undefined,
  });
}

// ─── MRR and booked revenue vs expenses ───

export interface RevenueExpensePoint {
  month: string;
  mrr: number;
  booked: number;
  expenses: number;
  netMrr: number;
  notYetBooked: number;
  partial: boolean;
  /** Entries behind a negative month or one booked above MRR; empty otherwise. */
  bookedEntries: BookedEntry[];
}

export interface BookedEntry {
  moveName: string;
  partner: string;
  amount: number;
}

export interface RevenueVsExpensesSummary {
  fullMonths: string[];
  burnMrr: number | null;
  burnBooked: number | null;
  avgExpenses: number | null;
  mrrTrendPerMonth: number | null;
  lastFullMonthMrr: number | null;
  /** Today's MRR (Overview's number); absent on an API older than round H. */
  currentMrr?: number | null;
  currentMrrMonth?: string | null;
  /** 0 = already there; null = MRR not growing. */
  monthsToBreakEven: number | null;
  notYetBookedTotal: number | null;
  /** Average expenses − current MRR; 0 once MRR covers costs. */
  gapToBreakEven: number | null;
}

export interface RevenueVsExpenses {
  currency: string;
  basis: string;
  series: RevenueExpensePoint[];
  summary: RevenueVsExpensesSummary;
  issues: FinanceDataIssue[];
}

export async function getRevenueVsExpenses(months?: number): Promise<RevenueVsExpenses> {
  return apiClient.get('/finance/revenue-vs-expenses', {
    params: months ? { months } : undefined,
  });
}

// ─── One month of MRR, per customer ───

export type ContractMonthState = 'started' | 'running' | 'ending' | 'renewed';

export interface MrrMonthContract {
  ref: string;
  odooSubscriptionId: number | null;
  plan: string | null;
  mrr: number;
  start: string | null;
  end: string | null;
  state: ContractMonthState;
}

export interface MrrMonthCustomer {
  customer: string;
  name: string;
  odooName: string | null;
  mrr: number;
  contractMrr: number;
  override: { note: string } | null;
  contracts: MrrMonthContract[];
}

export interface MrrMonth {
  month: string;
  currency: string;
  total: number;
  byTermTotal: number;
  overridesTotal: number;
  unexplained: number;
  payingCustomers: number;
  customers: MrrMonthCustomer[];
  odooBaseUrl: string;
}

export async function getMrrMonth(month: string): Promise<MrrMonth> {
  return apiClient.get('/finance/mrr-month', { params: { month } });
}

// ─── Tool costs by category ───

export type ToolCostCategoryId =
  | 'hosting_license'
  | 'it_general'
  | 'it_product'
  | 'marketing_tools'
  | 'sales_tools'
  | 'advertising';

export interface ToolCostCategory {
  id: ToolCostCategoryId;
  label: string;
  accountCode: string;
  accountName: string;
}

export interface ToolCostMonthPoint {
  month: string;
  byCategory: Record<ToolCostCategoryId, number>;
  total: number;
  /** Every recurring vendor (billed in 2 of the 3 months before, €50+) has its bill. Never true for the running month. */
  fullyBooked?: boolean;
  /** Recurring vendors with no bill booked yet for the month, biggest first. */
  missingBills?: { vendor: string; expected: number }[];
}

export interface ToolCostVendor {
  /** Reported name after aliases ("OpenAI" for "OpenAI Opco, LLC"). */
  vendor: string;
  /** The names the bills carried when an alias merged several. */
  aliases?: string[];
  /** One bill in the loaded history, big enough to be a yearly licence. */
  annual?: boolean;
  odooPartnerId: number | null;
  categoryId: ToolCostCategoryId;
  /** False when the vendor isn't in the mapping and the line was bucketed by its booked account. */
  mapped: boolean;
  byMonth: Record<string, number>;
  total: number;
}

/** A vendor with tool spend that the mapping doesn't cover yet. */
export interface UnmappedVendor {
  /** The name the bills carry; what an assignment is keyed on. */
  vendor: string;
  /** The name the rest of the report uses ("RB2B" for "Getemails, LLC RB2B"). Absent on older APIs. */
  displayName?: string;
  odooPartnerId: number | null;
  bookedAccountCodes: string[];
  total: number;
}

export interface VendorMapping {
  /** Null for a card-paid tool with no Odoo supplier; keyed on matchName. */
  odooPartnerId: number | null;
  vendorName: string;
  /** Short name matched against card-payment descriptions. */
  matchName: string | null;
  accountCode: string;
  /** Set when one supplier bills across accounts (LinkedIn Sales Navigator vs Ads). */
  whenAccountCode: string | null;
  isExcluded: boolean;
  /** 'sheet' = imported from the mapping sheet, 'manual' = assigned here. */
  source: string;
  note: string | null;
}

export interface MisbookedVendor {
  vendor: string;
  expectedAccountCode: string;
  expectedAccountName: string;
  actualAccountCode: string;
  actualAccountName: string;
  count: number;
  total: number;
  moveNames: string[];
}

export type ToolCostInsightType =
  | 'category_spike'
  | 'category_drop'
  | 'vendor_spike'
  | 'new_vendor'
  | 'vendor_gone'
  | 'annual_charge';

export interface ToolCostInsight {
  type: ToolCostInsightType;
  severity: 'warning' | 'info';
  month: string;
  categoryId: ToolCostCategoryId | null;
  vendor: string | null;
  current: number;
  baseline: number;
  message: string;
}

export interface ToolCosts {
  currency: string;
  categories: ToolCostCategory[];
  series: ToolCostMonthPoint[];
  vendors: ToolCostVendor[];
  unmapped: UnmappedVendor[];
  insights: ToolCostInsight[];
  misbooked: MisbookedVendor[];
  insightsMonth: string | null;
  /** Earliest cost-class journal line ever synced (YYYY-MM-DD); null = none. */
  firstBillDate: string | null;
  /** When the API worked the report out (it is cached for a few minutes). Absent on older APIs. */
  computedAt?: string;
  health: ToolCostsHealth;
}

/** Whether the report is doing what its captions claim. See the API module. */
export interface ToolCostsHealth {
  mappingSize: number;
  degraded: boolean;
  /** Share of spend categorised by booked account rather than by vendor (0–1). */
  unmappedShare: number;
  warnings: string[];
}

/** The tool-costs report normally answers in about a second; past this it is stopped and says so. */
export const TOOL_COSTS_TIMEOUT_MS = 30_000;

export async function getToolCosts(months?: number): Promise<ToolCosts> {
  try {
    return await apiClient.get('/finance/tool-costs', {
      params: months ? { months } : undefined,
      signal: AbortSignal.timeout(TOOL_COSTS_TIMEOUT_MS),
    });
  } catch (error) {
    if (error instanceof DOMException && (error.name === 'TimeoutError' || error.name === 'AbortError')) {
      const timeout = new Error(
        `Tool costs did not answer within ${TOOL_COSTS_TIMEOUT_MS / 1000} seconds, so the request was stopped. The API may be busy with a sync; try again in a minute.`,
      );
      throw Object.assign(timeout, { cause: error });
    }
    throw error;
  }
}

export async function getVendorMapping(): Promise<{ mapping: VendorMapping[] }> {
  return apiClient.get('/finance/tool-costs/mapping');
}

export interface SaveVendorMappingInput {
  /** Null for a card-paid tool; `matchName` then has to carry the vendor. */
  odooPartnerId: number | null;
  vendorName: string;
  /** Name to look for in card-payment descriptions; defaults to vendorName. */
  matchName?: string | null;
  accountCode: string;
  /** Scopes the row to one account, for suppliers that bill across several. */
  whenAccountCode?: string | null;
  isExcluded?: boolean;
  note?: string | null;
}

export async function saveVendorMapping(
  input: SaveVendorMappingInput
): Promise<{ entry: VendorMapping }> {
  return apiClient.patch('/finance/tool-costs/mapping', input);
}

/**
 * `whenAccountCode` says which of a vendor's rows to remove; without it the API
 * removes the vendor's default row, which is rarely the one you meant.
 */
export async function removeVendorMapping(input: {
  odooPartnerId: number | null;
  matchName?: string | null;
  whenAccountCode?: string | null;
}): Promise<{ deleted: boolean }> {
  const query = new URLSearchParams();
  if (input.whenAccountCode) query.set('whenAccountCode', input.whenAccountCode);
  if (input.odooPartnerId === null && input.matchName) query.set('matchName', input.matchName);
  const suffix = query.size > 0 ? `?${query}` : '';
  return apiClient.delete(
    `/finance/tool-costs/mapping/${input.odooPartnerId ?? 'name'}${suffix}`
  );
}
